import { useEffect, useRef, useState } from "react";

/**
 * Treibt das wandernde Band und meldet, welche Zeile gerade einschaltet
 * (13.9.2026, fuer `BausteinKarussell`, Vorbild workos.com).
 *
 * **Das Band laeuft in Schritten, nicht gleichmaessig.** In der Vorlage
 * nachgemessen (14.9.2026, Kreuzkorrelation der Zeilenprofile ueber
 * `workos-hero-component.webm`): 1.2 s Bewegung um genau eine Zeile, dann
 * 1.55 s Stillstand, Periode 2.75 s. Genauso liegt der Schaltmoment:
 *
 * | Zeitpunkt | Was geschieht |
 * |---|---|
 * | 0.0 s | Bewegung beginnt, die naechste Zeile rueckt nach |
 * | 1.2 s | Bewegung stoppt, die Zeile steht in der Mitte — Schalter noch AUS |
 * | 1.8 s | Der Schalter faehrt an (0.5 s), das Band steht dabei still |
 * | 2.75 s | Bewegung beginnt wieder |
 *
 * Die **0.6 s Stillstand vor dem Einschalten** sind der Kern: Sie machen aus
 * dem Umschalten eine Handlung statt eines Nebeneffekts der Bewegung. Ein Band,
 * das im Vorbeifahren schaltet, liest sich als Zustandsanzeige; eines, das
 * anhaelt und dann schaltet, als Vorgang.
 *
 * **Warum die Position hier und nicht in CSS entsteht.** Eine Schrittfolge in
 * Keyframes braucht ein Paar Stuetzstellen je Zeile — bei zwoelf Bausteinen
 * vierundzwanzig, fest verdrahtet an die Zeilenzahl. Ein spaeteres Kuerzen auf
 * zehn Bausteine liesse Band und Schaltmoment auseinanderlaufen, ohne dass es
 * jemandem auffiele. Die Schleife hier rechnet beides aus derselben Uhr.
 *
 * ⚠️ **Bei `prefers-reduced-motion` laeuft nichts.** Kein Transport, keine
 * Messung; die Komponente zeigt dann den Content-Zustand statisch.
 */

/**
 * Dauer eines Bewegungsschritts um eine Zeilenhoehe.
 *
 * 1500 statt der 1200 der Vorlage, und das ist Absicht: `easeInOutCubic` hat am
 * Rand die Steigung null und legt die letzten Prozent der Strecke in immer
 * kleineren Schritten zurueck. Sichtbar bewegt sich das Band deshalb nur etwa
 * vier Fuenftel der eingestellten Zeit — nachgemessen 0.9-1.0 s bei 1200 ms.
 * Mit 1500 ms trifft die SICHTBARE Bewegung die 1.2 s der Vorlage.
 */
const SCHRITT_MS = 1500;

/**
 * Ruhe zwischen zwei Schritten.
 *
 * ⚠️ In diese Zeit muessen BEIDE Vorgaenge passen: der Schalter ab
 * `VERZOEGERUNG_MS` und das Etikett ab `VERZOEGERUNG_MS + ETIKETT_MS`. Mit 1250
 * blieben nach dem Schalter nur 650 ms, das Etikett kam nie an die Reihe —
 * gemessen 14.9.2026: Text und Schalter wechselten exakt gleichzeitig, weil der
 * Zeitpunkt nie erreicht wurde und stattdessen der `oberhalb`-Zweig griff.
 */
const RUHE_MS = 1700;

/** Nach dem Anhalten vergeht diese Zeit, bis der Schalter umlegt. */
const VERZOEGERUNG_MS = 450;

/**
 * Wie lange nach dem Schalter das Etikett von «Bereit» auf «In Betrieb»
 * wechselt.
 *
 * **Der Text braucht eine eigene Zeitmarke, weil CSS ihn nicht verzoegern
 * kann.** Ein `transition-delay` wirkt nur auf interpolierbare Eigenschaften —
 * Farben. Das WORT wechselt React im selben Render wie der Schalter, also
 * augenblicklich. Gemessen 14.9.2026: Die Etikettenfarbe folgte brav nach
 * 650 ms, der Text stand schon vorher auf «In Betrieb», und genau das las sich
 * als «Etikett zuerst».
 *
 * 800 ms decken die Schalterdauer (500 ms) plus rund 300 ms Ruhe ab. Zusammen
 * mit `VERZOEGERUNG_MS` liegt der Etikettenwechsel bei 1250 ms nach dem
 * Anhalten und damit noch in der Ruhephase von 1700 ms.
 */
const ETIKETT_MS = 800;

const PERIODE_MS = SCHRITT_MS + RUHE_MS;

/**
 * Wie weit der Einfluss der Mitte reicht, in Zeilenhoehen.
 *
 * 2.5 heisst: Zweieinhalb Zeilen ueber und unter der Mitte laeuft die Naehe auf
 * null. Bei 6.5 sichtbaren Zeilen deckt das den ganzen Ausschnitt ab, ohne dass
 * die Randzeilen schon bei null ankommen — die Maske uebernimmt dort.
 */
const REICHWEITE_IN_ZEILEN = 2.5;

export type BandFokusOptions = {
    /** Anzahl der Zeilen im Band (Original plus Kopie). */
    zeilenAnzahl: number;
};

export type BandFokus = {
    /**
     * An das Band-Element haengen.
     *
     * `RefObject<HTMLDivElement>` und NICHT `<HTMLDivElement | null>`: Der
     * `ref`-Prop nimmt unter React 18 nur die erste Form an (die zweite ist ein
     * `MutableRefObject` und faellt mit TS2322 durch).
     */
    bandRef: React.RefObject<HTMLDivElement>;
    /** An die Liste haengen — sie wird von hier bewegt. */
    listeRef: React.RefObject<HTMLUListElement>;
    /** An jede Zeile haengen, mit ihrem Index. */
    zeileRef: (idx: number) => (el: HTMLLIElement | null) => void;
    /**
     * Naehe zur Bandmitte je Zeile: 1 = mittig, 0 = ausserhalb der Reichweite.
     * Leer, solange nicht gemessen wird (Server, erstes Bild, reduzierte
     * Bewegung).
     */
    naehe: readonly number[];
    /** Indizes der Zeilen, deren SCHALTER «an» steht. */
    aktivierte: ReadonlySet<number>;
    /**
     * Indizes der Zeilen, deren ETIKETT «In Betrieb» zeigt.
     *
     * Eine eigene Menge, weil der Text dem Schalter folgen soll und CSS das
     * nicht leisten kann: `transition-delay` wirkt nur auf Farben, nicht auf
     * Textinhalte. Siehe `ETIKETT_MS`.
     */
    beschriftet: ReadonlySet<number>;
    /**
     * `true` im Moment des Ruecksprungs und kurz danach.
     *
     * Die Komponente schaltet dann die Uebergaenge ab. Grund: Der Ruecksprung
     * ist geometrisch nahtlos, aber die DOM-Zeilen tauschen ihre Rolle — was
     * oben war, steht jetzt unten. Fuer EINEN Frame tragen die Kopien noch die
     * alte Klasse; die 500-ms-Transition startet, und der Schalter faehrt
     * danach sichtbar zurueck. Gemessen 14.9.2026: drei Knoepfe gleichzeitig von
     * 12 auf 0, unten im Band, wo sonst nie etwas schaltet.
     */
    springt: boolean;
};

export const useBandFokus = ({ zeilenAnzahl }: BandFokusOptions): BandFokus => {
    const bandRef = useRef<HTMLDivElement>(null);
    const listeRef = useRef<HTMLUListElement>(null);
    const zeilenRefs = useRef<(HTMLLIElement | null)[]>([]);
    const [naehe, setNaehe] = useState<readonly number[]>([]);
    const letzterZyklus = useRef<number | null>(null);
    /** Welche Zeile zuletzt geschaltet hat — haelt sie an, bis sie strikt oberhalb steht. */
    const fokusIdxRef = useRef<number | null>(null);
    /** Dasselbe fuer das Etikett, das dem Schalter mit Abstand folgt. */
    const etikettIdxRef = useRef<number | null>(null);
    const sprungBisFrame = useRef(0);
    const [aktivierte, setAktivierte] = useState<ReadonlySet<number>>(new Set());
    const [springt, setSpringt] = useState(false);
    const [beschriftet, setBeschriftet] = useState<ReadonlySet<number>>(new Set());

    const zeileRef = (idx: number) => (el: HTMLLIElement | null) => {
        zeilenRefs.current[idx] = el;
    };

    useEffect(() => {
        if (typeof window === "undefined" || typeof requestAnimationFrame === "undefined") {
            return;
        }
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        let laufend = true;
        let anforderung = 0;
        const start = performance.now();

        const messen = (jetzt: number) => {
            if (!laufend) {
                return;
            }
            const band = bandRef.current;
            const liste = listeRef.current;
            const ersteZeile = zeilenRefs.current.find(Boolean);
            if (band && liste && ersteZeile) {
                const zeilenHoehe = zeilenAbstandMessen(zeilenRefs.current.slice(0, zeilenAnzahl));
                if (zeilenHoehe <= 0) {
                    anforderung = requestAnimationFrame(messen);
                    return;
                }
                const verstrichen = jetzt - start;

                // **Der Ausgleich, damit die Fokuszeile GENAU auf der Bandmitte
                // sitzt.** Ohne ihn sass sie bei -0.09 Zeilenabstaenden, und die
                // Nachbarn lagen ungleich weit weg (1.09 gegen 0.91) — sichtbar
                // als 597 gegen 612 px Breite, obwohl n-1 und n+1 gleich gross
                // sein muessen.
                //
                // ⚠️ Der Ausgleich wird in GANZE Zeilen und einen Rest zerlegt.
                // Als eine Zahl in Pixeln verschob er die Nahtstelle: Das Band
                // sprang bei Versatz 8.91 auf -3.09 statt bei 12 auf 0, und nach
                // dem Sprung standen andere Bausteine an denselben Stellen —
                // gemessen 14.9.2026 als sichtbarer Bruch (drei Zeilen kippten
                // von AUS auf AN). Nur der REST darf frei verschieben; die ganzen
                // Zeilen gehoeren in den Modulo, sonst geht die Naht nicht auf.
                const ausgleichPx = band.clientHeight / 2 - ersteZeile.offsetHeight / 2;
                const ausgleichZeilen = Math.round(ausgleichPx / zeilenHoehe);
                const ausgleichRest = ausgleichPx - ausgleichZeilen * zeilenHoehe;

                // Die Liste ist verdoppelt: Nach der halben Zeilenzahl steht die
                // Kopie exakt dort, wo das Original begann. Weil der Zustand je
                // Bild aus der Position abgeleitet wird (`zeilenSindAn`), ist der
                // Ruecksprung dort in Geometrie UND Zustand nahtlos.
                const halbe = zeilenAnzahl / 2;
                const schritt = Math.floor(verstrichen / PERIODE_MS);
                const imSchritt = (verstrichen % PERIODE_MS) / SCHRITT_MS;
                const rohSchritt = schritt - ausgleichZeilen;
                // In JS ist `-1 % 12` gleich -1, nicht 11 — deshalb der Umweg.
                const zyklus = ((rohSchritt % halbe) + halbe) % halbe;
                // Der Ruecksprung: Der Zyklus faellt von halbe-1 auf 0.
                const springtJetzt = letzterZyklus.current !== null && zyklus < letzterZyklus.current;
                if (springtJetzt) {
                    sprungBisFrame.current = 3;
                }
                letzterZyklus.current = zyklus;
                const imSprung = sprungBisFrame.current > 0;
                if (imSprung) {
                    sprungBisFrame.current -= 1;
                }
                setSpringt(alt => (alt === imSprung ? alt : imSprung));
                const versatz = zyklus + Math.min(1, weichEin(imSchritt));
                liste.style.transform = `translateY(${ausgleichRest - versatz * zeilenHoehe}px)`;

                const bandKasten = band.getBoundingClientRect();
                const mitte = bandKasten.top + bandKasten.height / 2;
                const reichweite = zeilenHoehe * REICHWEITE_IN_ZEILEN;
                // Erst ALLE lesen, dann setzen: Ein Schreibzugriff zwischen zwei
                // `getBoundingClientRect` erzwingt ein neues Layout je Zeile.
                const roh = zeilenRefs.current.slice(0, zeilenAnzahl).map(el => {
                    if (!el) {
                        return { naehe: 0, relPos: 0, oberhalb: false, abstand: Infinity };
                    }
                    const kasten = el.getBoundingClientRect();
                    const zeilenMitte = kasten.top + kasten.height / 2;
                    return {
                        naehe: naeheAusAbstand(Math.abs(zeilenMitte - mitte), reichweite),
                        // Die UNTERKANTE gegen die Bandoberkante: Liegt sie
                        // darueber, ist von der Zeile nichts mehr zu sehen.
                        relPos: (kasten.bottom - bandKasten.top) / bandKasten.height,
                        // STRIKT ueber der Mitte, mit einem Viertel Zeilenhoehe
                        // Spielraum: Die Fokuszeile selbst steht auf der Mitte
                        // und darf hier NICHT als «durch» gelten — sonst greift
                        // in `zeilenSindAn` der Positionszweig, und die
                        // Wartezeit wird nie wirksam.
                        oberhalb: zeilenMitte < mitte - zeilenHoehe / 4,
                        abstand: Math.abs(zeilenMitte - mitte),
                    };
                });
                // Genau EINE Zeile ist die Fokuszeile: die mit dem kleinsten
                // Abstand zur Mitte. Eine Schwelle waehlte je nach Kurvenform
                // keine oder zwei.
                const naechster = Math.min(...roh.map(r => r.abstand));
                const gemessen = roh.map(r => ({
                    naehe: r.naehe,
                    relPos: r.relPos,
                    oberhalb: r.oberhalb,
                    istFokus: r.abstand === naechster,
                }));
                setNaehe(gemessen.map(g => g.naehe));
                // Der Schalter legt erst um, wenn das Band steht UND die
                // Verzoegerung abgelaufen ist — siehe Kopfkommentar.
                const imZyklus = verstrichen % PERIODE_MS;
                const darfSchalten = imZyklus >= SCHRITT_MS + VERZOEGERUNG_MS;
                if (darfSchalten && fokusIdxRef.current !== gemessen.findIndex(m => m.istFokus)) {
                    fokusIdxRef.current = gemessen.findIndex(m => m.istFokus);
                }
                const an = zeilenSindAn(gemessen, darfSchalten, fokusIdxRef.current);
                // Der Text folgt dem Schalter mit eigenem Abstand — siehe
                // `ETIKETT_MS`.
                const darfBeschriften = imZyklus >= SCHRITT_MS + VERZOEGERUNG_MS + ETIKETT_MS;
                if (darfBeschriften && etikettIdxRef.current !== fokusIdxRef.current) {
                    etikettIdxRef.current = fokusIdxRef.current;
                }
                const beschriftet = zeilenSindAn(gemessen, darfBeschriften, etikettIdxRef.current);
                setBeschriftet(alt => (gleicheMenge(alt, beschriftet) ? alt : beschriftet));
                setAktivierte(alt => (gleicheMenge(alt, an) ? alt : an));
            }
            anforderung = requestAnimationFrame(messen);
        };
        anforderung = requestAnimationFrame(messen);

        return () => {
            laufend = false;
            cancelAnimationFrame(anforderung);
        };
    }, [zeilenAnzahl]);

    return { bandRef, listeRef, zeileRef, naehe, aktivierte, beschriftet, springt };
};

/**
 * Der Schrittabstand wird GEMESSEN, nicht gesetzt (14.9.2026).
 *
 * Eine feste Konstante stand hier zuvor auf 12 (die `mb-3`-Marge), gemessen
 * sind es aber 17: `py-3` plus Zeilenhoehe ergibt 47 px, der Abstand von
 * Oberkante zu Oberkante 64 px. Das Band rueckte deshalb je Schritt um 59 statt
 * 64 px vor, und die Zeilen wanderten aus der Mitte — nachgemessen standen sie
 * bei -0.77 und +0.23 Zeilenabstaenden statt bei -1 und 0.
 *
 * Der Fehler war unsichtbar, weil er sich erst ueber mehrere Schritte
 * aufsummiert und die Fokusrechnung ihn nicht meldet: Sie misst brav, wo die
 * Zeilen SIND, nicht wo sie sein sollten.
 */
const zeilenAbstandMessen = (zeilen: readonly (HTMLLIElement | null)[]): number => {
    const [erste, zweite] = zeilen.filter(Boolean);
    if (!erste || !zweite) {
        return 0;
    }
    // `offsetTop` und NICHT `getBoundingClientRect`: Letzteres liefert die
    // Masse NACH der Skalierung, die diese Schleife selbst je Zeile setzt — die
    // Schrittweite haenge dann davon ab, welche Zeile gerade im Fokus steht, und
    // das Band ruckelte. `offsetTop` ignoriert `transform` und meldet das
    // Layoutraster.
    return zweite.offsetTop - erste.offsetTop;
};

/**
 * Blendet einen Bewegungsschritt weich ein und aus (`easeInOutCubic`).
 *
 * Die Vorlage beschleunigt und bremst deutlich: Die gemessene Verschiebung je
 * Bild steigt von 1 auf 14 px und faellt wieder auf 1. Ein linearer Schritt
 * sieht daneben mechanisch aus — er startet und stoppt hart.
 *
 * Werte ueber 1 gibt sie nicht zurueck; die Ruhephase ergibt sich daraus, dass
 * die Aufrufer den Wert bei 1 kappen.
 */
export const weichEin = (anteil: number): number => {
    if (anteil <= 0) {
        return 0;
    }
    if (anteil >= 1) {
        return 1;
    }
    return anteil < 0.5 ? 4 * anteil ** 3 : 1 - (-2 * anteil + 2) ** 3 / 2;
};

/**
 * Rechnet einen Abstand zur Bandmitte in eine Naehe zwischen 0 und 1 um.
 *
 * **Das Maximum muss schmal sein.** Eine reine Kosinus-Halbwelle hat um die
 * Mitte herum die Steigung null: Zwei benachbarte Zeilen bekommen dort fast
 * denselben Wert, und statt einer Fokuszeile entsteht ein Fokus-PAAR. Genau das
 * war der Zustand vor dieser Aenderung — im Vergleich der Aufnahmen gemessene
 * Kartenkontraste 252 und 243, also praktisch gleichauf, gegen 151 / 117 / 109
 * bei der Vorlage.
 *
 * Das Quadrieren zieht das Maximum zusammen: Bei halbem Abstand faellt die
 * Naehe von 0.50 auf 0.25, eine Zeile daneben ist also deutlich zurueck. Die
 * Vorlage zeigt genau dieses Gefaelle — ein klares Maximum, dann ein sanftes
 * Auslaufen ueber die weiteren Zeilen.
 */
export const naeheAusAbstand = (abstand: number, reichweite: number): number => {
    if (reichweite <= 0) {
        return 0;
    }
    const anteil = Math.min(1, abstand / reichweite);
    const halbwelle = (1 + Math.cos(anteil * Math.PI)) / 2;
    return halbwelle * halbwelle;
};

/** Was die Messschleife je Zeile und Bild ermittelt. */
export type ZeilenMessung = {
    /** Naehe zur Bandmitte: 1 = mittig, 0 = ausserhalb der Reichweite. */
    naehe: number;
    /**
     * Position der ZEILENUNTERKANTE im Band: 0 = auf der Bandoberkante,
     * 1 = auf der Bandunterkante. Werte unter 0 heissen: ganz herausgefahren.
     */
    relPos: number;
    /**
     * Steht die Zeilenmitte auf oder ueber der Bandmitte?
     *
     * Die Richtungssperre fuers Einschalten: Das Band laeuft nach oben, eine
     * Zeile hat die Mitte also genau dann erreicht, wenn dieser Wert `true`
     * wird. `naehe` allein genuegt nicht — sie ist symmetrisch und trifft auch
     * beim Hereinfahren zu.
     */
    oberhalb: boolean;
    /** Steht diese Zeile der Bandmitte am naechsten? Genau eine je Bild. */
    istFokus: boolean;
};

/**
 * Leitet ab, welche Zeilen «an» stehen — ohne Gedaechtnis (14.9.2026).
 *
 * **Die Regel ist die Position** (Entscheid Jan): Was ueber der Bandmitte
 * steht, ist an; was darunter steht, ist aus. Das Band laeuft nach oben — was
 * oben steht, ist durch; was unten steht, kommt noch. Die Fokuszeile selbst
 * schaltet um, sobald die Wartezeit im Stillstand abgelaufen ist, und wird
 * damit zur untersten «an»-Zeile.
 *
 * **Kein Zustandsspeicher, und das loest drei Fehler auf einmal.** Drei
 * Fassungen zuvor fuehrten eine Menge eingeschalteter Indizes mit. Daran hing:
 * ein Zustand, der beim Ruecksprung des Bands falsch mitwanderte; ein
 * Unterschied zwischen Original und Kopie derselben Kachel; und ein
 * Ruecksetzen, das mal zu frueh (sichtbar bei 34 % Deckkraft) und mal zu spaet
 * kam (drei bis vier Zeilen gleichzeitig an). Eine Position laesst sich in
 * jedem Bild neu ausrechnen — sie kann gar nicht driften.
 *
 * Gedaechtnis braucht nur die Wartezeit, und die kommt aus der Uhr.
 */
export const zeilenSindAn = (
    messungen: readonly ZeilenMessung[],
    fokusDarfSchalten: boolean,
    zuletztGeschaltet: number | null
): ReadonlySet<number> => {
    const an = new Set<number>();
    const fokusIdx = messungen.findIndex(m => m.istFokus);
    messungen.forEach(({ oberhalb }, idx) => {
        // Drei Wege, «an» zu sein:
        //   1. die Mitte passiert (`oberhalb`, strikt gemessen),
        //   2. Fokuszeile mit abgelaufener Wartezeit,
        //   3. zuletzt geschaltet und noch nicht ueber (1) hinaus.
        //
        // ⚠️ Der dritte Weg schliesst eine LUECKE, die sich nicht wegjustieren
        // laesst: Zwischen «Wartezeit vorbei» und «strikt oberhalb» liegt die
        // ganze Ruhephase. Dort gehoerte die Zeile weder zu (1) noch zu (2) und
        // fiel zurueck — gemessen 14.9.2026 siebenmal AN / AUS nach 1249 ms /
        // AN nach weiteren 600 ms. Ein groesserer Spielraum bei `oberhalb`
        // verschoebe die Luecke nur.
        if (oberhalb || (idx === fokusIdx && fokusDarfSchalten) || idx === zuletztGeschaltet) {
            an.add(idx);
        }
    });
    return an;
};

/**
 * Vergleicht zwei Mengen inhaltlich.
 *
 * Die Ableitung baut in jedem Bild eine neue Menge; React vergleicht per
 * Referenz und rendert sonst sechzigmal je Sekunde neu.
 */
const gleicheMenge = (a: ReadonlySet<number>, b: ReadonlySet<number>): boolean =>
    a.size === b.size && [...a].every(x => b.has(x));
